import {constrainHeroRowTravel} from './heroMotion.util.js';
import {startHeroScroll} from './heroScroll.util.js';
import {initializeLineCharts} from './lineChart.util.js';
import {bindShareClickTracking} from './shareTracking.util.js';

const {KitModule, registerModule} = window;

class Engineering extends KitModule {
    constructor($element) {
        super($element);

        this.setVars();
        this.cleanupLineCharts = initializeLineCharts(this.$element);
        this.initializeHeroGrain();
        if (this.$heroHeader) {
            this.heroResizeObserver = new ResizeObserver(() => {
                if (this.$heroArt) this.extendHeroArt();
                this.drawHeroGrain();
            });
            this.heroResizeObserver.observe(this.$heroHeader);
            if (this.$heroArt) this.heroResizeObserver.observe(this.$heroArt);
        }
        this.bindEvents();
        this.setupHeroScroll();
    }

    setVars() {
        this.copyTimers = new Map();
        this.heroSquares = this.$heroArt ? [...this.$heroArt.children] : [];
        this.heroBaseSquares = [...this.heroSquares];
        this.heroExtensionColumns = {left: [], right: []};
        this.heroMotion = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
        this.heroFrame = null;
        this.heroGeometry = [];
        this.heroTravelWeights = [];
        this.heroGap = 0;
        this.heroReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.$terms = this.$terms || [];
        this.termEdge = 16; // px a definition keeps from the viewport edge
    }

    initializeHeroGrain() {
        if (!this.$heroGrain) return;
        this.heroGrainContext = this.$heroGrain.getContext('2d');
        if (!this.heroGrainContext) return;
        const tile = document.createElement('canvas');
        tile.width = 256;
        tile.height = 256;
        const context = tile.getContext('2d');
        if (!context) return;
        const noise = context.createImageData(tile.width, tile.height);
        // Match the source grain's black/white pixels and varied alpha. Generate
        // once; reuse the same tile on resize instead of animating the noise.
        for (let index = 0; index < noise.data.length; index += 4) {
            const value = Math.random() < 0.5 ? 0 : 255;
            noise.data[index] = value;
            noise.data[index + 1] = value;
            noise.data[index + 2] = value;
            noise.data[index + 3] = Math.floor(Math.random() * 256);
        }
        context.putImageData(noise, 0, 0);
        this.heroGrainPattern = this.heroGrainContext.createPattern(tile, 'repeat');
        this.drawHeroGrain();
    }

    drawHeroGrain() {
        if (!this.heroGrainPattern) return;
        const width = this.$heroGrain.parentElement.clientWidth;
        const height = this.$heroGrain.parentElement.clientHeight;
        if (this.$heroGrain.width === width && this.$heroGrain.height === height) return;
        // One grain pixel per CSS pixel matches the original tiled image.
        this.$heroGrain.width = width;
        this.$heroGrain.height = height;
        this.heroGrainContext.fillStyle = this.heroGrainPattern;
        this.heroGrainContext.fillRect(0, 0, width, height);
    }

    bindEvents() {
        this.onHeroMove = this.onHeroMove.bind(this);
        this.resetHeroArt = this.resetHeroArt.bind(this);
        this.$heroHeader?.addEventListener('pointermove', this.onHeroMove, {passive: true});
        this.$heroHeader?.addEventListener('pointerleave', this.resetHeroArt);
        this.$heroHeader?.addEventListener('pointercancel', this.resetHeroArt);
        this.heroMotion.addEventListener('change', this.resetHeroArt);
        this.setupHeroScroll = this.setupHeroScroll.bind(this);
        this.heroReducedMotion.addEventListener('change', this.setupHeroScroll);
        window.addEventListener('resize', this.resetHeroArt, {passive: true});
        window.addEventListener('scroll', this.resetHeroArt, {passive: true});

        this.onCopyLink = this.onCopyLink.bind(this);
        (this.$copyLinks || []).forEach(($button) => {
            $button.hidden = false;
            $button.addEventListener('click', this.onCopyLink);
        });

        // Glossary terms show on hover/focus in CSS; JS adds tap-to-toggle,
        // Escape to dismiss, and keeps each definition inside the viewport.
        this.onTermShow = this.onTermShow.bind(this);
        this.onTermLeave = this.onTermLeave.bind(this);
        this.onTermClick = this.onTermClick.bind(this);
        this.onTermKeydown = this.onTermKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.$terms.forEach(($term) => {
            $term.addEventListener('pointerenter', this.onTermShow);
            $term.addEventListener('focusin', this.onTermShow);
            $term.addEventListener('pointerleave', this.onTermLeave);
            $term.addEventListener('focusout', this.onTermLeave);
            $term.addEventListener('click', this.onTermClick);
        });
        if (this.$terms.length) {
            document.addEventListener('keydown', this.onTermKeydown);
            document.addEventListener('click', this.onDocumentClick);
        }
    }

    // Fill the edges with varied squares, retaining each column's arrangement
    // across resizes so the artwork doesn't reshuffle while reading.
    extendHeroArt() {
        this.stopHeroScroll?.();
        this.stopHeroScroll = null;
        this.resetHeroArt();
        this.heroSquares.slice(this.heroBaseSquares.length).forEach(($square) => $square.remove());
        this.heroSquares = [...this.heroBaseSquares];
        const width = this.$heroArt.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(this.$heroArt).columnGap);
        this.heroGap = gap;
        const pitch = (width + gap) / 21;
        const rowTops = new Map(this.heroBaseSquares.filter(($square) => $square.offsetWidth > 0).map(($square) => [
            parseInt($square.style.gridRow, 10) - 1,
            $square.offsetTop,
        ]));
        const columns = Math.ceil(Math.max(0, this.$heroHeader.clientWidth - width) / (2 * pitch));
        const fragment = document.createDocumentFragment();
        for (let column = 0; column < columns; column++) {
            for (const side of ['left', 'right']) {
                if (!this.heroExtensionColumns[side][column]) {
                    const tones = [1, 2, 2, 3, 3, 4, 5];
                    this.heroExtensionColumns[side][column] = Array.from({length: 11}, (_, row) => ({
                        row,
                        tone: tones[Math.floor(Math.random() * tones.length)],
                    })).filter(({row}) => Math.random() < (row < 3 || row > 7 ? 0.75 : 0.35));
                }
                this.heroExtensionColumns[side][column].forEach(({row, tone}) => {
                    const $square = document.createElement('span');
                    $square.className = `tone-${tone} engineering-hero-art__extension`;
                    $square.dataset.row = row;
                    $square.style.left = `${side === 'left' ? -(column + 1) * pitch : width + gap + column * pitch}px`;
                    $square.style.top = `${rowTops.get(row)}px`;
                    $square.style.width = `${pitch - gap}px`;
                    fragment.append($square);
                    this.heroSquares.push($square);
                });
            }
        }
        this.$heroArt.append(fragment);
        const squares = this.heroSquares.map(($square, index) => {
            this.heroTravelWeights[index] ??= 1.5 + Math.random() * 2.5;
            return {
                row: Number($square.dataset.row ?? (parseInt($square.style.gridRow, 10) - 1)),
                left: $square.offsetLeft,
                top: $square.offsetTop,
                width: $square.offsetWidth,
                height: $square.offsetHeight,
                travel: $square.offsetWidth * this.heroTravelWeights[index],
                index,
            };
        }).filter(({width}) => width > 0);
        const offsets = constrainHeroRowTravel({squares, gap});
        // Both endpoints retain a gutter, so shared scroll progress cannot
        // make neighboring squares cross, even when reversing direction.
        this.heroGeometry = squares.map((square, index) => ({
            ...square,
            element: this.heroSquares[square.index],
            x: offsets[index],
        }));
        this.setupHeroScroll();
    }

    setupHeroScroll() {
        this.stopHeroScroll?.();
        this.stopHeroScroll = null;
        this.heroPointer = null;
        this.renderHeroArt();
        if (!this.$heroArt || this.heroReducedMotion.matches) return;
        this.stopHeroScroll = startHeroScroll({target: this.$heroHeader, items: this.heroGeometry});
    }

    onHeroMove(event) {
        if (!this.$heroArt || !this.heroMotion.matches || event.pointerType === 'touch') return;
        this.heroPointer = {x: event.clientX, y: event.clientY};
        this.renderHeroArt();
    }

    renderHeroArt() {
        if (!this.$heroArt || this.heroFrame !== null) return;
        this.heroFrame = window.requestAnimationFrame(() => {
            this.heroFrame = null;
            const pointer = this.heroPointer;
            const bounds = pointer ? this.$heroArt.getBoundingClientRect() : null;
            const translations = this.heroGeometry.map((square) => {
                let x = 0;
                let y = 0;
                if (pointer) {
                    // Geometry is cached on resize and excludes scroll/hover transforms.
                    const dx = bounds.left + square.left + square.width / 2 - pointer.x;
                    const dy = bounds.top + square.top + square.height / 2 - pointer.y;
                    const distance = Math.hypot(dx, dy);
                    const strength = Math.min(8, this.heroGap * 0.2) * Math.max(0, 1 - distance / 100) ** 2;
                    x = dx / (distance || 1) * strength;
                    y = dy / (distance || 1) * strength;
                }
                return `${x}px ${y}px`;
            });
            this.heroGeometry.forEach(({element}, index) => {
                element.style.translate = translations[index];
            });
        });
    }

    resetHeroArt() {
        if (!this.heroPointer) return;
        this.heroPointer = null;
        this.renderHeroArt();
    }

    onTermShow(event) {
        const $term = event.currentTarget;
        $term.classList.remove('is-dismissed');
        this.positionTerm($term);
    }

    onTermLeave(event) {
        const $term = event.currentTarget;
        if (event.type === 'focusout' && $term.contains(event.relatedTarget)) return;
        $term.classList.remove('is-dismissed');
        if (event.type === 'focusout') $term.classList.remove('is-open');
    }

    // Touch has no hover, so a tap toggles the definition open.
    onTermClick(event) {
        const $term = event.currentTarget;
        if (!event.target.closest('.engineering-term__word')) return;
        const isOpen = !$term.classList.contains('is-open');
        this.closeTerms();
        $term.classList.toggle('is-open', isOpen);
        $term.classList.toggle('is-dismissed', !isOpen);
        if (isOpen) this.positionTerm($term);
    }

    onDocumentClick(event) {
        if (!event.target.closest('.engineering-term')) this.closeTerms();
    }

    onTermKeydown(event) {
        if (event.key !== 'Escape') return;
        this.$terms.forEach(($term) => {
            if ($term.matches(':hover, :focus-within, .is-open')) $term.classList.add('is-dismissed');
            $term.classList.remove('is-open');
        });
    }

    closeTerms() {
        this.$terms.forEach(($term) => $term.classList.remove('is-open'));
    }

    /**
     * Shift a term's definition sideways so it stays inside the viewport.
     */
    positionTerm($term) {
        const $definition = $term.querySelector('.engineering-term__definition');
        if (!$definition) return;
        // Measured from the term, not the definition, whose transform may be
        // mid-transition.
        const termRect = $term.getBoundingClientRect();
        const left = termRect.left + (termRect.width - $definition.offsetWidth) / 2;
        const right = left + $definition.offsetWidth;
        const maxRight = document.documentElement.clientWidth - this.termEdge;
        let shift = 0;
        if (left < this.termEdge) shift = this.termEdge - left;
        else if (right > maxRight) shift = maxRight - right;
        $term.style.setProperty('--term-shift', `${shift}px`);
    }

    async onCopyLink(event) {
        const $button = event.currentTarget;
        // Modified clicks retain the anchor's native open-in-new-tab behavior.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        let message;
        let copied = false;
        try {
            await navigator.clipboard.writeText($button.dataset.shareUrl);
            message = $button.classList.contains('engineering-heading-link') ? 'Section link copied' : 'Link copied';
            copied = true;
        } catch {
            message = 'Could not copy link';
        }
        if (!this.$shareStatus?.isConnected) return;
        this.$shareStatus.textContent = message;
        $button.querySelector('.article-share__feedback').textContent = message;
        $button.classList.toggle('is-copied', copied);
        $button.classList.toggle('has-feedback', !copied);
        window.clearTimeout(this.copyTimers.get($button));
        this.copyTimers.set($button, window.setTimeout(() => {
            $button.classList.remove('has-feedback', 'is-copied');
            this.$shareStatus.textContent = '';
            this.copyTimers.delete($button);
        }, 2000));
    }

    unmount() {
        this.cleanupLineCharts();
        this.heroResizeObserver?.disconnect();
        this.stopHeroScroll?.();
        window.cancelAnimationFrame(this.heroFrame);
        this.heroFrame = null;
        this.heroSquares.forEach(($square) => {
            $square.style.translate = '';
        });
        this.$heroHeader?.removeEventListener('pointermove', this.onHeroMove);
        this.$heroHeader?.removeEventListener('pointerleave', this.resetHeroArt);
        this.$heroHeader?.removeEventListener('pointercancel', this.resetHeroArt);
        this.heroMotion.removeEventListener('change', this.resetHeroArt);
        this.heroReducedMotion.removeEventListener('change', this.setupHeroScroll);
        window.removeEventListener('resize', this.resetHeroArt);
        window.removeEventListener('scroll', this.resetHeroArt);
        this.copyTimers.forEach((timer) => window.clearTimeout(timer));
        (this.$copyLinks || []).forEach(($button) => $button.removeEventListener('click', this.onCopyLink));
        document.removeEventListener('keydown', this.onTermKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        this.$terms.forEach(($term) => {
            $term.removeEventListener('pointerenter', this.onTermShow);
            $term.removeEventListener('focusin', this.onTermShow);
            $term.removeEventListener('pointerleave', this.onTermLeave);
            $term.removeEventListener('focusout', this.onTermLeave);
            $term.removeEventListener('click', this.onTermClick);
        });
    }
}

registerModule('Engineering', Engineering);
bindShareClickTracking();
